/**
 * Moving stock, and buying more of it.
 *
 * Everything here is append-only and everything here can REFUSE.
 *
 * ── append-only ─────────────────────────────────────────────────────────
 *
 * A stock record's whole value is that it cannot be quietly revised to agree
 * with the shelf. So no operation edits a movement and none removes one: a
 * correction is another movement, a mis-receipt is a negative adjustment with
 * a reason, and the trail of both stays. The bin quantity is derived FROM the
 * movements in the sense that every change to it is accompanied by one; the
 * quantity is carried on the item as well because reading a year of movements
 * to draw a table is not free, and the test pins that the two agree.
 *
 * ── refusals ────────────────────────────────────────────────────────────
 *
 * The platform has the final word elsewhere in this application; here the
 * register does, and it says no to four things in particular:
 *
 *   - issuing more than is in the bin — the bin is the physical shelf, and a
 *     system that lets it go negative has stopped describing the world;
 *   - receiving more than was ordered — which is how a delivery error becomes
 *     an inventory error and then an invoice nobody can reconcile;
 *   - approving above an authority's limit — the chain exists to be a
 *     constraint, and one that can be exceeded is decoration;
 *   - approving what one has raised oneself — the oldest control there is.
 *
 * A refusal returns the reason as a sentence, and the caller shows it
 * verbatim. None of them is a validation message about a form field; each is
 * a statement about what the airport's own rules do not permit.
 */
import { t, tn } from "../i18n";
import type { IntakeRow, ReceiptRow } from "./storesIntake";
import type { ItemFields, StoresOp } from "./storesOps";
import {
  categoriesOf,
  heldAt as heldAtStore,
  onHand,
  STORE_CATEGORIES,
  type Movement,
  type MovementKind,
  type PurchaseOrder,
  type Requisition,
  type RequisitionLine,
  type Reservation,
  type StockItem,
  type StoresRegister,
} from "./stores";

/**
 * What an operation came to. `ops` is the same change said as steps the
 * platform can take (storesOps.ts): on the sample the register is kept, on a
 * platform that holds the stores the steps are sent instead.
 */
export type Result =
  | { ok: true; register: StoresRegister; note?: string; ops?: StoresOp[] }
  | { ok: false; why: string };

export interface Actor {
  name: string;
  role?: string;
}

const refuse = (why: string): Result => ({ ok: false, why });

let counter = 0;
/** Ids are only ever compared with each other, never parsed. */
function newId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

function clone(reg: StoresRegister): StoresRegister {
  return {
    ...reg,
    items: reg.items.map((i) => ({ ...i, bins: i.bins.map((b) => ({ ...b })) })),
    movements: [...reg.movements],
    reservations: reg.reservations.map((r) => ({ ...r })),
    requisitions: reg.requisitions.map((q) => ({ ...q, lines: [...q.lines], approvals: [...q.approvals] })),
    orders: reg.orders.map((o) => ({ ...o, lines: o.lines.map((l) => ({ ...l })), receipts: [...o.receipts] })),
  };
}

function find(reg: StoresRegister, sku: string): StockItem | undefined {
  return reg.items.find((i) => i.sku === sku);
}

function heldAt(item: StockItem, location: string): number {
  return item.bins.filter((b) => b.location === location).reduce((n, b) => n + b.onHand, 0);
}

/** Add to a bin, creating it when the line has never been held there. */
function addToBin(item: StockItem, location: string, bin: string | undefined, qty: number): void {
  const existing = item.bins.find((b) => b.location === location && (bin === undefined || b.bin === bin));
  if (existing) existing.onHand += qty;
  else item.bins.push({ location, bin, onHand: qty });
}

/** Take from the bins at a location, fullest first; the caller has checked there is enough. */
function takeFromBins(item: StockItem, location: string, qty: number): void {
  let left = qty;
  const here = item.bins.filter((b) => b.location === location).sort((a, b) => b.onHand - a.onHand);
  for (const b of here) {
    const take = Math.min(b.onHand, left);
    b.onHand -= take;
    left -= take;
    if (left <= 0) break;
  }
}

/** A line's details as a step carries them. */
export function itemFields(item: Partial<Omit<StockItem, "id" | "bins">>): ItemFields {
  const out: ItemFields = {};
  const keys = [
    "name", "category", "uom", "unitCost", "manufacturer", "partNumber", "barcode", "min", "max", "leadDays",
    "supplier", "contractRef", "expiresAt", "note", "hazardous", "serves",
  ] as const;
  for (const k of keys) {
    if (item[k] !== undefined) (out as Record<string, unknown>)[k] = item[k];
  }
  if (item.critical) out.criticalWhy = item.critical.why;
  return out;
}

/** Every step a run of results reports, in order. */
function stepsOf(...results: Result[]): StoresOp[] {
  return results.flatMap((r) => (r.ok ? (r.ops ?? []) : []));
}

function record(reg: StoresRegister, m: Omit<Movement, "id">): void {
  reg.movements = [...reg.movements, { ...m, id: newId("mv") }];
}

/* ── moving stock ─────────────────────────────────────────────────────── */

export interface IssueInput {
  sku: string;
  qty: number;
  location: string;
  workOrder?: string;
  note?: string;
  at?: string;
}

/**
 * Issue to a job.
 *
 * Where a reservation exists for the same line and work order, the issue is
 * counted against it rather than beside it — otherwise a part promised and
 * then handed over would be subtracted twice and the shelf would appear to
 * have lost stock it still holds.
 */
export function issue(reg: StoresRegister, inp: IssueInput, who: Actor, now = new Date()): Result {
  if (inp.qty <= 0) return refuse(t("An issue must be of at least one unit."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  const here = heldAt(item, inp.location);
  if (here < inp.qty) {
    return refuse(
      t("{location} holds {here} {uom}. Issuing {qty} would take the bin below nothing, which the shelf cannot do.", {
        location: inp.location,
        here,
        uom: item.uom,
        qty: inp.qty,
      }),
    );
  }
  takeFromBins(item, inp.location, inp.qty);
  record(next, {
    at: inp.at ?? now.toISOString(),
    sku: inp.sku,
    kind: "issue",
    qty: -inp.qty,
    location: inp.location,
    by: who.name,
    workOrder: inp.workOrder,
    note: inp.note,
  });
  let against = 0;
  if (inp.workOrder) {
    let left = inp.qty;
    for (const r of next.reservations) {
      if (r.sku !== inp.sku || r.workOrder !== inp.workOrder || r.released) continue;
      const owed = r.qty - r.issued;
      if (owed <= 0) continue;
      const take = Math.min(owed, left);
      r.issued += take;
      against += take;
      left -= take;
      if (left <= 0) break;
    }
  }
  return {
    ok: true,
    register: next,
    note: against
      ? t("{n} issued against the reservation for {wo}.", { n: against, wo: inp.workOrder ?? "" })
      : undefined,
    ops: [{ op: "issue", sku: inp.sku, qty: inp.qty, location: inp.location, workOrder: inp.workOrder, note: inp.note, at: inp.at }],
  };
}

/** Back to the shelf, unused. */
export function returnToStore(reg: StoresRegister, inp: IssueInput, who: Actor, now = new Date()): Result {
  if (inp.qty <= 0) return refuse(t("A return must be of at least one unit."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  addToBin(item, inp.location, undefined, inp.qty);
  record(next, {
    at: inp.at ?? now.toISOString(),
    sku: inp.sku,
    kind: "return",
    qty: inp.qty,
    location: inp.location,
    by: who.name,
    workOrder: inp.workOrder,
    note: inp.note,
  });
  return {
    ok: true,
    register: next,
    ops: [{ op: "return", sku: inp.sku, qty: inp.qty, location: inp.location, workOrder: inp.workOrder, note: inp.note, at: inp.at }],
  };
}

/**
 * An adjustment, which always carries a reason.
 *
 * A stock figure changed without one is indistinguishable from a theft, a
 * mis-count and a typing error, and the difference between those three is the
 * only thing anybody investigating cares about.
 */
export function adjust(
  reg: StoresRegister,
  inp: { sku: string; qty: number; location: string; reason: string },
  who: Actor,
  now = new Date(),
): Result {
  if (inp.qty === 0) return refuse(t("An adjustment of nothing is not a record of anything."));
  if (!inp.reason.trim()) return refuse(t("An adjustment must say why. A figure changed without a reason cannot be investigated later."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  const here = heldAt(item, inp.location);
  if (inp.qty < 0 && here < -inp.qty) {
    return refuse(t("{location} holds {here} {uom}; it cannot be reduced by {qty}.", { location: inp.location, here, uom: item.uom, qty: -inp.qty }));
  }
  if (inp.qty > 0) addToBin(item, inp.location, undefined, inp.qty);
  else takeFromBins(item, inp.location, -inp.qty);
  record(next, {
    at: now.toISOString(),
    sku: inp.sku,
    kind: "adjust",
    qty: inp.qty,
    location: inp.location,
    by: who.name,
    note: inp.reason,
  });
  return {
    ok: true,
    register: next,
    ops: [{ op: "adjust", sku: inp.sku, qty: inp.qty, location: inp.location, reason: inp.reason }],
  };
}

/**
 * A physical count.
 *
 * The counted figure replaces the bin, and the DIFFERENCE is written as the
 * movement — so the record says what the count found rather than only what it
 * left behind. A count that agrees is still recorded: it is the only evidence
 * the accuracy figure has, and a system that logs only discrepancies scores
 * itself on a sample of its own failures.
 */
export function count(
  reg: StoresRegister,
  inp: { sku: string; location: string; counted: number; note?: string },
  who: Actor,
  now = new Date(),
): Result {
  if (inp.counted < 0) return refuse(t("A count cannot be negative."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  const here = heldAt(item, inp.location);
  const difference = inp.counted - here;
  if (difference > 0) addToBin(item, inp.location, undefined, difference);
  else if (difference < 0) takeFromBins(item, inp.location, -difference);
  record(next, {
    at: now.toISOString(),
    sku: inp.sku,
    kind: "count",
    qty: difference,
    location: inp.location,
    by: who.name,
    note: inp.note,
  });
  item.counted = { at: now.toISOString().slice(0, 10), by: who.name, variance: difference };
  return {
    ok: true,
    register: next,
    ops: [{ op: "count", sku: inp.sku, location: inp.location, counted: inp.counted, note: inp.note }],
    note:
      difference === 0
        ? t("The count agreed with the register.")
        : t("The count differed by {n}; the register has been brought to the counted figure and the difference recorded.", {
            n: difference > 0 ? `+${difference}` : String(difference),
          }),
  };
}

/** Between two stores. Two movements, so both shelves have their own record. */
export function transfer(
  reg: StoresRegister,
  inp: { sku: string; qty: number; from: string; to: string; note?: string },
  who: Actor,
  now = new Date(),
): Result {
  if (inp.from === inp.to) return refuse(t("A transfer must be between two different stores."));
  if (inp.qty <= 0) return refuse(t("A transfer must be of at least one unit."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  const here = heldAt(item, inp.from);
  if (here < inp.qty) {
    return refuse(t("{location} holds {here} {uom}, which is less than {qty}.", { location: inp.from, here, uom: item.uom, qty: inp.qty }));
  }
  takeFromBins(item, inp.from, inp.qty);
  addToBin(item, inp.to, undefined, inp.qty);
  const at = now.toISOString();
  record(next, { at, sku: inp.sku, kind: "transfer", qty: -inp.qty, location: inp.from, otherLocation: inp.to, by: who.name, note: inp.note });
  record(next, { at, sku: inp.sku, kind: "transfer", qty: inp.qty, location: inp.to, otherLocation: inp.from, by: who.name, note: inp.note });
  return {
    ok: true,
    register: next,
    ops: [{ op: "transfer", sku: inp.sku, qty: inp.qty, fromLocation: inp.from, toLocation: inp.to, note: inp.note }],
  };
}

/* ── promising stock to a job ─────────────────────────────────────────── */

/**
 * Reserve against a work order.
 *
 * A reservation is allowed to exceed what is on the shelf. That is not an
 * oversight: the job needs what it needs, and a system that refuses to record
 * a requirement it cannot meet has destroyed the only evidence that the
 * shortage exists. What it must never do is pretend the stock is there — see
 * `available()`, which goes negative, and the reorder run, which picks it up.
 */
export function reserve(
  reg: StoresRegister,
  inp: { sku: string; qty: number; workOrder: string; neededAt?: string },
  who: Actor,
  now = new Date(),
): Result {
  if (inp.qty <= 0) return refuse(t("A reservation must be of at least one unit."));
  if (!inp.workOrder.trim()) return refuse(t("A reservation must name the work order it is for."));
  const next = clone(reg);
  const item = find(next, inp.sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku: inp.sku }));
  const r: Reservation = {
    id: newId("rs"),
    sku: inp.sku,
    qty: inp.qty,
    workOrder: inp.workOrder.trim(),
    at: now.toISOString(),
    by: who.name,
    neededAt: inp.neededAt,
    issued: 0,
  };
  next.reservations = [...next.reservations, r];
  const short = inp.qty - (onHand(item) - reg.reservations.filter((x) => x.sku === inp.sku && !x.released).reduce((n, x) => n + (x.qty - x.issued), 0));
  return {
    ok: true,
    register: next,
    note:
      short > 0
        ? t("Recorded. {n} of these are not on the shelf, so the line now shows a shortage against {wo}.", { n: short, wo: r.workOrder })
        : undefined,
    ops: [{ op: "reserve", sku: inp.sku, qty: inp.qty, workOrder: r.workOrder, neededAt: inp.neededAt }],
  };
}

export function releaseReservation(reg: StoresRegister, id: string, reason: string, who: Actor, now = new Date()): Result {
  if (!reason.trim()) return refuse(t("Releasing a reservation must say why, so the job's record shows what changed."));
  const next = clone(reg);
  const r = next.reservations.find((x) => x.id === id);
  if (!r) return refuse(t("That reservation is no longer on the register."));
  if (r.released) return refuse(t("That reservation has already been released."));
  r.released = { at: now.toISOString(), by: who.name, reason: reason.trim() };
  return { ok: true, register: next, ops: [{ op: "release", id, reason: reason.trim() }] };
}

/* ── buying ───────────────────────────────────────────────────────────── */

/** The next number in a series, continuing whatever the register already holds. */
function nextNumber(existing: string[], prefix: string, year: number): string {
  let highest = 0;
  for (const n of existing) {
    const m = /(\d+)\s*$/.exec(n);
    if (m) highest = Math.max(highest, Number(m[1]));
  }
  return `${prefix}-${year}-${String(highest + 1).padStart(4, "0")}`;
}

export function raiseRequisition(
  reg: StoresRegister,
  inp: { lines: RequisitionLine[]; department?: string; note?: string },
  who: Actor,
  now = new Date(),
): Result {
  const lines = inp.lines.filter((l) => l.qty > 0);
  if (!lines.length) return refuse(t("A requisition needs at least one line with a quantity."));
  const unknown = lines.find((l) => !find(reg, l.sku));
  if (unknown) return refuse(t("{sku} is not in the catalog.", { sku: unknown.sku }));
  const blank = lines.find((l) => !l.why.trim());
  if (blank) return refuse(t("Every line must say why it is being bought. A purchase nobody can account for is one nobody approves twice."));
  const next = clone(reg);
  const q: Requisition = {
    id: newId("rq"),
    number: nextNumber(next.requisitions.map((x) => x.number), "RQ", now.getUTCFullYear()),
    raisedAt: now.toISOString(),
    raisedBy: who.name,
    department: inp.department,
    lines,
    status: "submitted",
    approvals: [],
    note: inp.note,
  };
  next.requisitions = [...next.requisitions, q];
  return {
    ok: true,
    register: next,
    note: t("Raised as {number}.", { number: q.number }),
    ops: [{ op: "requisition.raise", lines, department: inp.department, note: inp.note }],
  };
}

/** What a requisition comes to, where every line carries a price. */
export function requisitionValue(q: Requisition, reg: StoresRegister): number | null {
  let total = 0;
  for (const l of q.lines) {
    const cost = l.estimatedUnitCost ?? find(reg, l.sku)?.unitCost;
    if (cost === undefined) return null;
    total += cost * l.qty;
  }
  return total;
}

export interface Authority {
  role: string;
  /** null = no limit */
  limit: number | null;
}

/**
 * Approve or reject.
 *
 * Two refusals, and both are older than any software: nobody approves their
 * own requisition, and nobody approves above their limit. The second needs the
 * value, and a requisition whose value cannot be computed cannot be approved
 * at all — an approval of an unknown amount is not an approval.
 */
export function decideRequisition(
  reg: StoresRegister,
  inp: { number: string; decision: "approved" | "rejected"; authority: Authority; note?: string },
  who: Actor,
  now = new Date(),
): Result {
  const next = clone(reg);
  const q = next.requisitions.find((x) => x.number === inp.number);
  if (!q) return refuse(t("{number} is not on the register.", { number: inp.number }));
  if (q.status !== "submitted") {
    return refuse(t("{number} is {status} and is not waiting on a decision.", { number: q.number, status: q.status }));
  }
  if (q.raisedBy === who.name) {
    return refuse(t("{who} raised {number}. Nobody approves their own requisition.", { who: who.name, number: q.number }));
  }
  if (inp.decision === "approved") {
    const value = requisitionValue(q, next);
    if (value === null) {
      return refuse(t("{number} has a line with no price, so its value is unknown. An approval of an unknown amount is not an approval.", { number: q.number }));
    }
    if (inp.authority.limit !== null && value > inp.authority.limit) {
      return refuse(
        t("{value} {currency} is above the {role}'s limit of {limit}. It goes to the next authority.", {
          value: Math.round(value).toLocaleString(),
          currency: next.currency,
          role: inp.authority.role,
          limit: Math.round(inp.authority.limit).toLocaleString(),
        }),
      );
    }
  }
  q.approvals = [
    ...q.approvals,
    {
      at: now.toISOString(),
      by: who.name,
      role: inp.authority.role,
      decision: inp.decision,
      limit: inp.authority.limit,
      note: inp.note,
    },
  ];
  q.status = inp.decision === "approved" ? "approved" : "rejected";
  return {
    ok: true,
    register: next,
    ops: [
      {
        op: "requisition.decide",
        number: q.number,
        decision: inp.decision,
        role: inp.authority.role,
        limit: inp.authority.limit,
        note: inp.note,
      },
    ],
  };
}

export function orderFromRequisition(
  reg: StoresRegister,
  inp: { number: string; supplier: string; expectedAt?: string; contractRef?: string },
  who: Actor,
  now = new Date(),
): Result {
  const next = clone(reg);
  const q = next.requisitions.find((x) => x.number === inp.number);
  if (!q) return refuse(t("{number} is not on the register.", { number: inp.number }));
  if (q.status !== "approved") {
    return refuse(t("{number} is {status}. Only an approved requisition becomes an order.", { number: q.number, status: q.status }));
  }
  if (!inp.supplier.trim()) return refuse(t("An order must name the supplier it goes to."));
  const lines = q.lines.map((l) => ({
    sku: l.sku,
    qty: l.qty,
    unitCost: l.estimatedUnitCost ?? find(next, l.sku)?.unitCost ?? 0,
    received: 0,
  }));
  const order: PurchaseOrder = {
    id: newId("po"),
    number: nextNumber(next.orders.map((x) => x.number), "PO", now.getUTCFullYear()),
    supplier: inp.supplier.trim(),
    contractRef: inp.contractRef,
    raisedAt: now.toISOString(),
    raisedBy: who.name,
    expectedAt: inp.expectedAt,
    status: "open",
    lines,
    receipts: [],
    requisitionNumber: q.number,
    currency: next.currency,
  };
  next.orders = [...next.orders, order];
  q.status = "ordered";
  q.orderNumber = order.number;
  return {
    ok: true,
    register: next,
    note: t("Ordered as {number}.", { number: order.number }),
    ops: [
      { op: "requisition.order", number: q.number, supplier: inp.supplier.trim(), expectedAt: inp.expectedAt, contractRef: inp.contractRef },
    ],
  };
}

/**
 * Receive a delivery.
 *
 * What is rejected at the gate is recorded and does NOT go on the shelf — it
 * stays outstanding on the order, which is the truth: the airport ordered it,
 * paid for nothing, and is still waiting. Systems that book the whole delivery
 * in and raise a credit note later are how a bin ends up holding cracked
 * transformers on paper.
 */
export function receiveDelivery(
  reg: StoresRegister,
  inp: {
    orderNumber: string;
    deliveryNote?: string;
    lines: { sku: string; qty: number; location: string; bin?: string }[];
    rejected?: { sku: string; qty: number; why: string }[];
  },
  who: Actor,
  now = new Date(),
): Result {
  const next = clone(reg);
  const order = next.orders.find((o) => o.number === inp.orderNumber);
  if (!order) return refuse(t("{number} is not on the register.", { number: inp.orderNumber }));
  if (order.status === "cancelled") return refuse(t("{number} was canceled.", { number: order.number }));
  const taking = inp.lines.filter((l) => l.qty > 0);
  if (!taking.length && !(inp.rejected ?? []).length) return refuse(t("A receipt must record something received or something rejected."));
  for (const l of taking) {
    const ol = order.lines.find((x) => x.sku === l.sku);
    if (!ol) return refuse(t("{sku} is not on {number}.", { sku: l.sku, number: order.number }));
    const owed = ol.qty - ol.received;
    if (l.qty > owed) {
      return refuse(
        t("{number} has {owed} of {sku} outstanding; {qty} cannot be received against it. Record the surplus as a separate order or send it back.", {
          number: order.number,
          owed,
          sku: l.sku,
          qty: l.qty,
        }),
      );
    }
  }
  const at = now.toISOString();
  for (const l of taking) {
    const item = find(next, l.sku);
    if (!item) return refuse(t("{sku} is not in the catalog.", { sku: l.sku }));
    const ol = order.lines.find((x) => x.sku === l.sku)!;
    ol.received += l.qty;
    addToBin(item, l.location, l.bin, l.qty);
    record(next, {
      at,
      sku: l.sku,
      kind: "receipt",
      qty: l.qty,
      location: l.location,
      bin: l.bin,
      by: who.name,
      reference: order.number,
      unitCost: ol.unitCost,
    });
  }
  order.receipts = [
    ...order.receipts,
    { at, by: who.name, deliveryNote: inp.deliveryNote, lines: taking, rejected: inp.rejected },
  ];
  const outstanding = order.lines.some((l) => l.received < l.qty);
  order.status = outstanding ? "part_received" : "received";
  return {
    ok: true,
    register: next,
    ops: [
      {
        op: "order.receive",
        number: order.number,
        deliveryNote: inp.deliveryNote,
        lines: taking,
        rejected: inp.rejected?.length ? inp.rejected : undefined,
      },
    ],
    note: outstanding
      ? t("Received. {number} still has {n} outstanding.", {
          number: order.number,
          n: order.lines.filter((l) => l.received < l.qty).length,
        })
      : t("{number} is complete.", { number: order.number }),
  };
}

export function cancelOrder(reg: StoresRegister, number: string, reason: string, who: Actor): Result {
  if (!reason.trim()) return refuse(t("Canceling an order must say why."));
  const next = clone(reg);
  const order = next.orders.find((o) => o.number === number);
  if (!order) return refuse(t("{number} is not on the register.", { number }));
  if (order.receipts.length) {
    return refuse(t("{number} has already been delivered against and cannot be canceled. Close the outstanding lines instead.", { number }));
  }
  order.status = "cancelled";
  record(next, {
    at: new Date().toISOString(),
    sku: order.lines[0]?.sku ?? "",
    kind: "adjust",
    qty: 0,
    location: "",
    by: who.name,
    reference: order.number,
    note: t("Order canceled: {reason}", { reason: reason.trim() }),
  });
  return { ok: true, register: next, ops: [{ op: "order.cancel", number, reason: reason.trim() }] };
}

/* ── the catalogue ────────────────────────────────────────────────────── */

export function addItem(reg: StoresRegister, item: Omit<StockItem, "id">, who: Actor): Result {
  if (!item.sku.trim()) return refuse(t("A line needs a catalog number."));
  if (!item.name.trim()) return refuse(t("A line needs a name."));
  if (find(reg, item.sku.trim())) return refuse(t("{sku} is already in the catalog.", { sku: item.sku.trim() }));
  if (item.min < 0 || item.max < 0) return refuse(t("A reorder point and a maximum cannot be negative."));
  if (item.max > 0 && item.max < item.min) return refuse(t("The maximum cannot be below the reorder point."));
  const next = clone(reg);
  next.items = [...next.items, { ...item, sku: item.sku.trim(), name: item.name.trim(), id: newId("st") }];
  record(next, {
    at: new Date().toISOString(),
    sku: item.sku.trim(),
    kind: "adjust",
    qty: 0,
    location: item.bins[0]?.location ?? "",
    by: who.name,
    note: t("Added to the catalog."),
  });
  const { bins, ...details } = item;
  return {
    ok: true,
    register: next,
    ops: [
      {
        op: "item.create",
        item: { ...itemFields(details), sku: item.sku.trim(), name: item.name.trim() },
        bins: bins.map((b) => ({ location: b.location, bin: b.bin, qty: b.onHand })),
      },
    ],
  };
}

/**
 * The whole replenishment run, as requisitions — one per supplier.
 *
 * One requisition per supplier rather than one per line, because that is what
 * becomes one order, one delivery and one invoice. A run that raises thirty
 * requisitions has converted a five-minute job into a week of approvals.
 */
export function raiseReplenishment(
  reg: StoresRegister,
  run: { supplier: string; lines: { item: StockItem; qty: number; state: string; requested?: string }[] }[],
  who: Actor,
  now = new Date(),
): Result {
  if (!run.length) return refuse(t("Nothing is below its reorder point."));
  /*
   * A line somebody has already asked for is not asked for again. Without
   * this, pressing the button twice — which is exactly what a person does
   * when the screen shows no sign of the first press — puts two requisitions
   * for the same part in front of the same approver.
   */
  const already = run.flatMap((g) => g.lines).filter((l) => l.requested).length;
  const fresh = run
    .map((g) => ({ ...g, lines: g.lines.filter((l) => !l.requested) }))
    .filter((g) => g.lines.length > 0);
  if (!fresh.length) {
    return refuse(
      tn(
        already,
        "The one line below its reorder point has already been asked for. Nothing was raised twice.",
        "Every line below its reorder point has already been asked for. Nothing was raised twice.",
      ),
    );
  }
  let register = reg;
  const numbers: string[] = [];
  /*
   * On the platform the reorder list is the platform's own (GET
   * /v1/stores/reorder), narrowed to the lines this screen showed: it may have
   * seen stock arrive since, and a quantity worked out here from an older
   * register would order what is already on the shelf.
   */
  const ops: StoresOp[] = [{ op: "replenish", skus: fresh.flatMap((g) => g.lines.map((l) => l.item.sku)) }];
  for (const group of fresh) {
    const result = raiseRequisition(
      register,
      {
        lines: group.lines.map((l) => ({
          sku: l.item.sku,
          qty: l.qty,
          why: t("Replenishment: {state}.", { state: l.state }),
          estimatedUnitCost: l.item.unitCost,
        })),
        note: t("Raised by the replenishment run for {supplier}.", { supplier: group.supplier }),
      },
      who,
      now,
    );
    if (!result.ok) return result;
    register = result.register;
    numbers.push(register.requisitions[register.requisitions.length - 1].number);
  }
  return {
    ok: true,
    register,
    ops,
    note:
      t("Raised {n} for approval: {list}.", {
        n: numbers.length === 1 ? t("one requisition") : t("{n} requisitions", { n: numbers.length }),
        list: numbers.join(", "),
      }) + (already > 0 ? " " + tn(already, "{n} line was already asked for and was not raised again.", "{n} lines were already asked for and were not raised again.") : ""),
  };
}

export const MOVEMENT_LABEL: Record<MovementKind, string> = {
  receipt: "Received",
  issue: "Issued",
  return: "Returned",
  adjust: "Adjusted",
  transfer: "Transferred",
  count: "Counted",
};

/* ── getting a real store into the register ───────────────────────────── */

/**
 * Apply a planned intake.
 *
 * `basis` decides what the quantity differences MEAN, and it is the whole
 * reason this takes an argument rather than guessing:
 *
 *   "opening" — the register is being told what is on the shelves. The
 *   movements are adjustments carrying the file's name as their reason, and
 *   nothing is recorded as counted, because nobody counted anything for the
 *   benefit of this register.
 *
 *   "count" — somebody walked the shelves. The movements are counts, each
 *   line's `counted` stamp is set, and the differences feed the accuracy
 *   figure, which is exactly what they are evidence of.
 *
 * Rows the plan marked as errors are skipped, never guessed at, and the count
 * of what was skipped comes back so the caller can say so.
 */
export function applyIntake(
  reg: StoresRegister,
  plan: { rows: IntakeRow[] },
  inp: { basis: "opening" | "count"; source: string },
  who: Actor,
  now = new Date(),
): Result {
  const usable = plan.rows.filter((r) => r.status !== "same" && r.status !== "error");
  const skipped = plan.rows.filter((r) => r.status === "error").length;
  if (usable.length === 0) {
    return refuse(
      skipped > 0
        ? t("Every line in the file has something wrong with it. Nothing was applied.")
        : t("There is nothing in the file to apply."),
    );
  }
  let next = clone(reg);
  let added = 0;
  let changed = 0;
  let moved = 0;
  const ops: StoresOp[] = [];

  for (const row of usable) {
    if (row.item) {
      /* new to the catalogue: added with its opening quantity already in the bin */
      const result = addItem(next, { ...row.item }, who);
      if (!result.ok) return result;
      next = result.register;
      added += 1;
      /* the platform records the opening quantity as the same kind of movement this does */
      for (const op of stepsOf(result)) {
        ops.push(
          op.op === "item.create"
            ? {
                ...op,
                basis: inp.basis === "count" ? "count" : "adjust",
                reference: inp.source,
                openingNote:
                  inp.basis === "count"
                    ? t("Counted on to the register from {source}.", { source: inp.source })
                    : t("Opening balance from {source}.", { source: inp.source }),
              }
            : op,
        );
      }
      if ((row.qty ?? 0) > 0) {
        record(next, {
          at: now.toISOString(),
          sku: row.sku,
          kind: inp.basis === "count" ? "count" : "adjust",
          qty: row.qty ?? 0,
          location: row.location ?? "",
          bin: row.bin,
          by: who.name,
          reference: inp.source,
          note:
            inp.basis === "count"
              ? t("Counted on to the register from {source}.", { source: inp.source })
              : t("Opening balance from {source}.", { source: inp.source }),
        });
        moved += 1;
      }
      if (inp.basis === "count") {
        const item = next.items.find((i) => i.sku === row.sku);
        if (item) item.counted = { at: now.toISOString().slice(0, 10), by: who.name, variance: 0 };
      }
      continue;
    }

    const item = next.items.find((i) => i.sku === row.sku);
    if (!item) continue;
    let touched = false;
    for (const c of row.changes) {
      if (c.field === "qty") continue;
      touched = true;
      switch (c.field) {
        case "name":
          item.name = c.after;
          break;
        case "uom":
          item.uom = c.after;
          break;
        case "min":
          item.min = Number(c.after);
          break;
        case "max":
          item.max = Number(c.after);
          break;
        case "unitCost":
          item.unitCost = Number(c.after);
          break;
        case "leadDays":
          item.leadDays = Number(c.after);
          break;
        case "supplier":
          item.supplier = c.after;
          break;
        case "partNumber":
          item.partNumber = c.after;
          break;
        case "manufacturer":
          item.manufacturer = c.after;
          break;
        case "expiresAt":
          item.expiresAt = c.after;
          break;
        case "category": {
          const found = STORE_CATEGORIES.find((x) => x.label === c.after || x.key === c.after);
          if (found) item.category = found.key;
          break;
        }
        case "critical":
          item.critical = c.after === t("yes") ? { why: t("marked critical in {source}", { source: inp.source }) } : undefined;
          break;
        default:
          break;
      }
    }
    if (touched) {
      changed += 1;
      /* the corrected fields as they now stand; quiet, because the load is the record of why */
      const held = item as unknown as Record<string, unknown>;
      const fields = row.changes.map((c) => c.field).filter((f) => f !== "qty" && f !== "critical");
      const changes = itemFields(Object.fromEntries(fields.map((f) => [f, held[f]])) as Partial<StockItem>);
      if (row.changes.some((c) => c.field === "critical")) changes.criticalWhy = item.critical?.why ?? null;
      ops.push({ op: "item.edit", sku: row.sku, changes, quiet: true });
    }

    /* the quantity, as whichever kind of movement the caller said this is */
    const qtyChange = row.changes.find((c) => c.field === "qty");
    if (qtyChange) {
      const target = Number(qtyChange.after);
      const location = row.location ?? item.bins[0]?.location ?? "";
      const result =
        inp.basis === "count"
          ? count(next, { sku: row.sku, location, counted: target, note: t("Stock count loaded from {source}.", { source: inp.source }) }, who, now)
          : adjust(
              next,
              {
                sku: row.sku,
                location,
                qty: target - heldAt(item, location),
                reason: t("Opening balance from {source}.", { source: inp.source }),
              },
              who,
              now,
            );
      if (!result.ok) return result;
      next = result.register;
      ops.push(...stepsOf(result));
      moved += 1;
    }
  }

  const said: string[] = [];
  if (added) said.push(t("{n} added to the catalog", { n: added }));
  if (changed) said.push(t("{n} corrected", { n: changed }));
  if (moved) said.push(inp.basis === "count" ? t("{n} counted", { n: moved }) : t("{n} given an opening balance", { n: moved }));
  if (skipped) said.push(t("{n} left out because they could not be read", { n: skipped }));
  return { ok: true, register: next, note: said.join(", ") + ".", ops };
}

/**
 * A delivery with no purchase order behind it.
 *
 * Direct purchases, emergency buys, free issue from a contractor, a transfer
 * in from another airport: all real, none of them preceded by an order this
 * register raised, and all of them refused by `receiveDelivery`, which insists
 * on an order number.
 *
 * What it insists on instead is a REASON. A receipt with no order behind it is
 * the first thing an auditor pulls, and "it just appeared on the shelf" is the
 * answer that turns a question into a finding. The movements are marked so
 * they can be listed later.
 */
export function receiveDirect(
  reg: StoresRegister,
  inp: {
    lines: { sku: string; qty: number; location: string; bin?: string; unitCost?: number }[];
    supplier?: string;
    deliveryNote?: string;
    reason: string;
  },
  who: Actor,
  now = new Date(),
): Result {
  const lines = inp.lines.filter((l) => l.qty > 0);
  if (!lines.length) return refuse(t("A delivery must record at least one line with a quantity."));
  if (!inp.reason.trim()) {
    return refuse(
      t("A delivery with no purchase order behind it must say why it came in that way — a direct purchase, an emergency, free issue from a contractor."),
    );
  }
  const next = clone(reg);
  for (const l of lines) {
    const item = next.items.find((i) => i.sku === l.sku);
    if (!item) return refuse(t("{sku} is not in the catalog.", { sku: l.sku }));
  }
  const at = now.toISOString();
  for (const l of lines) {
    const item = next.items.find((i) => i.sku === l.sku)!;
    addToBin(item, l.location, l.bin, l.qty);
    if (l.unitCost !== undefined) item.unitCost = l.unitCost;
    record(next, {
      at,
      sku: l.sku,
      kind: "receipt",
      qty: l.qty,
      location: l.location,
      bin: l.bin,
      by: who.name,
      reference: inp.deliveryNote || t("no delivery note"),
      unitCost: l.unitCost,
      note: t("Received without a purchase order{supplier}: {reason}", {
        supplier: inp.supplier ? t(" from {name}", { name: inp.supplier }) : "",
        reason: inp.reason.trim(),
      }),
    });
  }
  return {
    ok: true,
    register: next,
    note: t("{n} booked in without an order. It is recorded as such.", { n: lines.length }),
    ops: [{ op: "receive.direct", lines, supplier: inp.supplier, deliveryNote: inp.deliveryNote, reason: inp.reason.trim() }],
  };
}

/**
 * An order somebody raised somewhere else.
 *
 * Procurement raises purchase orders in their own system; the stores still
 * have to book the delivery in against something and the desk still has to
 * show what is coming. Recorded with the system it came from, so nobody
 * mistakes it for an order this register raised and approved.
 */
export function recordExternalOrder(
  reg: StoresRegister,
  inp: {
    number: string;
    supplier: string;
    system: string;
    lines: { sku: string; qty: number; unitCost?: number }[];
    expectedAt?: string;
    reference?: string;
  },
  who: Actor,
  now = new Date(),
): Result {
  if (!inp.number.trim()) return refuse(t("An order needs the number it carries in the system it was raised in."));
  if (!inp.supplier.trim()) return refuse(t("An order must name the supplier it goes to."));
  if (!inp.system.trim()) return refuse(t("Say which system this order was raised in, so nobody goes looking for it here."));
  const lines = inp.lines.filter((l) => l.qty > 0);
  if (!lines.length) return refuse(t("An order needs at least one line with a quantity."));
  const next = clone(reg);
  if (next.orders.some((o) => o.number === inp.number.trim())) {
    return refuse(t("{number} is already on the register.", { number: inp.number.trim() }));
  }
  for (const l of lines) {
    if (!next.items.find((i) => i.sku === l.sku)) return refuse(t("{sku} is not in the catalog.", { sku: l.sku }));
  }
  const order: PurchaseOrder = {
    id: newId("po"),
    number: inp.number.trim(),
    supplier: inp.supplier.trim(),
    raisedAt: now.toISOString(),
    raisedBy: who.name,
    expectedAt: inp.expectedAt,
    status: "open",
    lines: lines.map((l) => ({
      sku: l.sku,
      qty: l.qty,
      unitCost: l.unitCost ?? next.items.find((i) => i.sku === l.sku)?.unitCost ?? 0,
      received: 0,
    })),
    receipts: [],
    currency: next.currency,
    raisedElsewhere: { system: inp.system.trim(), reference: inp.reference },
  };
  next.orders = [...next.orders, order];
  return {
    ok: true,
    register: next,
    note: t("{number} recorded as raised in {system}. Its delivery can now be booked in.", {
      number: order.number,
      system: order.raisedElsewhere!.system,
    }),
    ops: [
      {
        op: "order.external",
        number: order.number,
        supplier: order.supplier,
        system: inp.system.trim(),
        lines,
        expectedAt: inp.expectedAt,
        reference: inp.reference,
      },
    ],
  };
}

/**
 * Correct a line's details.
 *
 * The catalogue number is not editable: it is the key the movements, the
 * reservations and the order lines all find the line by, and renaming it in
 * place would orphan every one of them. A line numbered wrongly is added
 * again under the right number and the old one obsoleted.
 */
export function editItem(
  reg: StoresRegister,
  sku: string,
  changes: Partial<Pick<StockItem, "name" | "category" | "uom" | "unitCost" | "min" | "max" | "leadDays" | "supplier" | "contractRef" | "partNumber" | "manufacturer" | "expiresAt" | "note">> & { critical?: { why: string } | null },
  who: Actor,
  now = new Date(),
): Result {
  const next = clone(reg);
  const item = next.items.find((i) => i.sku === sku);
  if (!item) return refuse(t("{sku} is not in the catalog.", { sku }));
  const min = changes.min ?? item.min;
  const max = changes.max ?? item.max;
  if (min < 0 || max < 0) return refuse(t("A reorder point and a maximum cannot be negative."));
  if (max > 0 && max < min) return refuse(t("The maximum cannot be below the reorder point."));
  if (changes.critical === null) item.critical = undefined;
  else if (changes.critical) item.critical = changes.critical;
  const { critical: _critical, ...rest } = changes;
  Object.assign(item, rest);
  record(next, {
    at: now.toISOString(),
    sku,
    kind: "adjust",
    qty: 0,
    location: item.bins[0]?.location ?? "",
    by: who.name,
    note: t("Line details corrected: {fields}.", {
      fields: Object.keys(rest).join(", ") || t("criticality"),
    }),
  });
  const fields = itemFields(rest);
  if (changes.critical !== undefined) fields.criticalWhy = changes.critical?.why ?? null;
  return { ok: true, register: next, ops: [{ op: "item.edit", sku, changes: fields }] };
}

/**
 * Book in a planned delivery — against an order, or without one.
 *
 * Every line ADDS to what the shelf already holds. A stock load says what is
 * there; a delivery says what has just arrived, and the register adds it to
 * what was there before. The two are never the same operation, and the
 * screens name them differently for that reason.
 *
 * Rows the plan marked as errors are left out, never guessed at, and what was
 * left out is reported. Against an order, the quantities are checked a second
 * time by `receiveDelivery`, which owns that rule; this function does not
 * reimplement it.
 */
export function applyReceipt(
  reg: StoresRegister,
  plan: { rows: ReceiptRow[] },
  inp: { orderNumber?: string; supplier?: string; deliveryNote?: string; reason?: string },
  who: Actor,
  now = new Date(),
): Result {
  const ready = plan.rows.filter((r) => r.status !== "error" && (r.qty ?? 0) > 0);
  const skipped = plan.rows.length - ready.length;
  if (!ready.length) {
    return refuse(
      skipped > 0
        ? t("No line of this delivery can be booked in. Nothing was changed.")
        : t("There is nothing in the delivery to book in."),
    );
  }
  /*
   * Parts the catalogue has never held are created first, empty, and the
   * delivery then books stock on to them like any other line. Created rather
   * than refused, because the part is physically at the gate: a register that
   * can only receive what it already knows about is one the storekeeper works
   * around, and stock worked around is stock nobody records.
   */
  let reg2 = reg;
  const created: string[] = [];
  const ops: StoresOp[] = [];
  for (const row of ready) {
    if (row.status !== "new" || !row.item) continue;
    const made = addItem(reg2, { ...row.item, bins: [] }, who);
    if (!made.ok) return made;
    reg2 = made.register;
    ops.push(...stepsOf(made));
    created.push(row.sku);
  }

  const lines = ready.map((r) => ({
    sku: r.sku,
    qty: r.qty!,
    location: r.location,
    bin: r.bin,
    unitCost: r.unitCost,
  }));

  const result = inp.orderNumber
    ? receiveDelivery(
        reg2,
        { orderNumber: inp.orderNumber, deliveryNote: inp.deliveryNote, lines: lines.map(({ unitCost: _c, ...l }) => l) },
        who,
        now,
      )
    : receiveDirect(
        reg2,
        { lines, supplier: inp.supplier, deliveryNote: inp.deliveryNote, reason: inp.reason ?? "" },
        who,
        now,
      );
  if (!result.ok) return result;

  const units = lines.reduce((n, l) => n + l.qty, 0);
  return {
    ok: true,
    register: result.register,
    ops: [...ops, ...stepsOf(result)],
    note:
      t("{n} booked in, {units} units on to the shelf.", { n: lines.length, units }) +
      (created.length > 0
        ? " " + tn(created.length, "{n} part was new to the catalog and was added.", "{n} parts were new to the catalog and were added.")
        : "") +
      (result.note ? ` ${result.note}` : "") +
      (skipped > 0 ? " " + tn(skipped, "{n} line was left out.", "{n} lines were left out.") : ""),
  };
}

/** Add several lines to an order raised elsewhere, in one go. */
export function recordExternalOrderLines(
  reg: StoresRegister,
  inp: {
    number: string;
    supplier: string;
    system: string;
    lines: { sku: string; qty: number; unitCost?: number }[];
    expectedAt?: string;
    reference?: string;
  },
  who: Actor,
  now = new Date(),
): Result {
  return recordExternalOrder(reg, inp, who, now);
}

/* ── the store's own categories ───────────────────────────────────────── */

/**
 * Add a category.
 *
 * The key is derived from the name rather than typed, because a key is what
 * every line, every import column and the executive board match on, and a
 * storekeeper should not have to know that. Two rules:
 *
 *  - a name that collides with one already in use is refused, rather than
 *    quietly becoming a second category with the same label and a different
 *    key, which is how a store ends up reporting the same thing twice;
 *  - a name is required to say what goes in it. A category with no
 *    description is one only its author can file against correctly.
 */
export function addCategory(
  reg: StoresRegister,
  inp: { label: string; what: string },
  who: Actor,
): Result {
  const label = inp.label.trim();
  if (!label) return refuse(t("A category needs a name."));
  if (!inp.what.trim()) {
    return refuse(t("Say what goes in it. A category nobody can describe is one only its author files against correctly."));
  }
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
  if (!key) return refuse(t("That name gives no key the register can use. Use letters or numbers in it."));
  const existing = categoriesOf(reg);
  if (existing.some((c) => c.key === key || c.label.toLowerCase() === label.toLowerCase())) {
    return refuse(t("{label} is already a category.", { label }));
  }
  const next = clone(reg);
  next.categories = [
    ...(next.categories ?? []),
    { key, label, what: inp.what.trim(), added: true, by: who.name, at: new Date().toISOString().slice(0, 10) },
  ];
  return {
    ok: true,
    register: next,
    note: t("{label} added. Lines can be filed under it now.", { label }),
    ops: [{ op: "category.add", label, what: inp.what.trim() }],
  };
}

/**
 * Remove one the storekeeper added.
 *
 * A category with lines filed under it is not removed — the lines would be
 * left pointing at something that no longer exists, and the register would
 * show them under a bare key with no name. Move them first, and the refusal
 * says how many there are. The built-in categories are never removable: the
 * executive board names them, and a store that deleted "Tools" would take a
 * column off a board it does not own.
 */
export function removeCategory(reg: StoresRegister, key: string, _who: Actor): Result {
  if (STORE_CATEGORIES.some((c) => c.key === key)) {
    return refuse(t("That category is one the platform ships with and the executive board names. It cannot be removed."));
  }
  const held = reg.items.filter((i) => i.category === key && !i.obsoleted).length;
  if (held > 0) {
    return refuse(
      tn(
        held,
        "{n} line is filed under it. Move it to another category first.",
        "{n} lines are filed under it. Move them to another category first.",
      ),
    );
  }
  const next = clone(reg);
  next.categories = (next.categories ?? []).filter((c) => c.key !== key);
  return { ok: true, register: next, ops: [{ op: "category.remove", key }] };
}

/* ── the counter ──────────────────────────────────────────────────────── */

export interface BasketLine {
  sku: string;
  qty: number;
  /** which store it comes off; the line's own store when not given */
  location?: string;
}

export interface CounterOutcome {
  sku: string;
  name: string;
  wanted: number;
  issued: number;
  location: string;
  /** set when less than the whole line was handed over */
  short?: string;
}

export interface CounterResult {
  ok: true;
  register: StoresRegister;
  outcomes: CounterOutcome[];
  note: string;
  /** what was handed over or taken back, as steps (see Result) */
  ops?: StoresOp[];
}

/**
 * Hand a basket of parts over the counter, against one job.
 *
 * Two decisions here are about how a stores counter actually works rather
 * than how a form usually does.
 *
 * **It hands over what is there.** A technician asking for five and finding
 * three takes the three and comes back for the rest; a counter that refuses
 * the whole line because it cannot fill it sends them away with nothing and
 * the register no wiser. So each line issues what the shelf holds and the
 * shortfall is reported, per line, in words.
 *
 * **One bad line does not stop the rest.** The basket is not a transaction —
 * the parts are physically being handed across — so a line that cannot be
 * filled at all is recorded as such and the others still go.
 *
 * Every line still goes through `issue()`, so the reservation accounting, the
 * movements and the bin arithmetic are the same ones the rest of the register
 * uses. Nothing here writes to a bin directly.
 */
export function issueBasket(
  reg: StoresRegister,
  inp: { workOrder: string; lines: BasketLine[]; note?: string },
  who: Actor,
  now = new Date(),
): Result | CounterResult {
  const lines = inp.lines.filter((l) => l.qty > 0);
  if (!lines.length) return refuse(t("Nothing has been scanned into the basket yet."));
  if (!inp.workOrder.trim()) {
    return refuse(t("Scan or type the work order first. Parts handed over against no job cannot be costed to one later."));
  }

  let register = reg;
  const outcomes: CounterOutcome[] = [];
  const ops: StoresOp[] = [];
  for (const line of lines) {
    const item = register.items.find((i) => i.sku === line.sku);
    if (!item) {
      outcomes.push({ sku: line.sku, name: line.sku, wanted: line.qty, issued: 0, location: "", short: t("not in the catalog") });
      continue;
    }
    const location = line.location ?? [...item.bins].sort((a, b) => b.onHand - a.onHand)[0]?.location ?? "";
    const here = heldAtStore(item, location);
    const giving = Math.min(line.qty, here);
    if (giving <= 0) {
      outcomes.push({
        sku: item.sku,
        name: item.name,
        wanted: line.qty,
        issued: 0,
        location,
        short: t("none on that shelf"),
      });
      continue;
    }
    const result = issue(
      register,
      { sku: item.sku, qty: giving, location, workOrder: inp.workOrder.trim(), note: inp.note },
      who,
      now,
    );
    if (!result.ok) {
      outcomes.push({ sku: item.sku, name: item.name, wanted: line.qty, issued: 0, location, short: result.why });
      continue;
    }
    register = result.register;
    ops.push(...stepsOf(result));
    outcomes.push({
      sku: item.sku,
      name: item.name,
      wanted: line.qty,
      issued: giving,
      location,
      short:
        giving < line.qty
          ? t("{n} short — that is all this shelf held", { n: line.qty - giving })
          : undefined,
    });
  }

  const handed = outcomes.filter((o) => o.issued > 0);
  const units = handed.reduce((n, o) => n + o.issued, 0);
  const short = outcomes.filter((o) => o.short);
  return {
    ok: true,
    register,
    outcomes,
    ops,
    note:
      t("{n} handed over against {wo}, {units} units.", {
        n: handed.length,
        wo: inp.workOrder.trim(),
        units,
      }) + (short.length ? " " + tn(short.length, "{n} line was short.", "{n} lines were short.") : ""),
  };
}

/** The same across the counter the other way: parts coming back unused. */
export function returnBasket(
  reg: StoresRegister,
  inp: { workOrder: string; lines: BasketLine[]; note?: string },
  who: Actor,
  now = new Date(),
): Result | CounterResult {
  const lines = inp.lines.filter((l) => l.qty > 0);
  if (!lines.length) return refuse(t("Nothing has been scanned into the basket yet."));
  let register = reg;
  const outcomes: CounterOutcome[] = [];
  const ops: StoresOp[] = [];
  for (const line of lines) {
    const item = register.items.find((i) => i.sku === line.sku);
    if (!item) {
      outcomes.push({ sku: line.sku, name: line.sku, wanted: line.qty, issued: 0, location: "", short: t("not in the catalog") });
      continue;
    }
    const location = line.location ?? [...item.bins].sort((a, b) => b.onHand - a.onHand)[0]?.location ?? register.locations[0]?.code ?? "";
    const result = returnToStore(
      register,
      { sku: item.sku, qty: line.qty, location, workOrder: inp.workOrder.trim() || undefined, note: inp.note },
      who,
      now,
    );
    if (!result.ok) {
      outcomes.push({ sku: item.sku, name: item.name, wanted: line.qty, issued: 0, location, short: result.why });
      continue;
    }
    register = result.register;
    ops.push(...stepsOf(result));
    outcomes.push({ sku: item.sku, name: item.name, wanted: line.qty, issued: line.qty, location });
  }
  const back = outcomes.filter((o) => o.issued > 0);
  return {
    ok: true,
    register,
    outcomes,
    ops,
    note: t("{n} put back, {units} units.", {
      n: back.length,
      units: back.reduce((n, o) => n + o.issued, 0),
    }),
  };
}
